// libraries
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react'

// components
import RichText from '/react4xp/components/RichText'
import ButtonRegular from '/react4xp/components/ButtonRegular'

// utils
import { useSmoothScroll } from '/lib/reactutils/utils'

interface Props {
   tag: string
   title: string
   intro: string
   slidingLink?: {
      text: string
      url: string
   }
   img: {
      src: string
      srcMobile: string
      alt: string
   } | null
   imgLarge: {
      src: string
      srcMobile: string
      alt: string
   } | null
   isLarge: boolean
   color: string
}

export default function LandingPageHero(props: Props) {
   const { tag, title, intro, slidingLink, img, imgLarge, isLarge, color } = props

   const ref = useRef<HTMLDivElement>(null)
   const figureRef = useRef<HTMLDivElement>(null)
   const gridRowRef = useRef<HTMLDivElement>(null)
   const gridContainerRef = useRef<HTMLDivElement>(null)

   const [gradientPercentage, setGradientPercentage] = useState(50)
   const [figureRightOffset, setFigureRightOffset] = useState(0)
   const [isMobile, setIsMobile] = useState<boolean>(false)

   const backgroundColor = {
      primary: color === 'green' ? '#D2DFD2' : '#EDE1D4',
      secondary: color === 'green' ? '#EDF2ED' : '#F8F3EE',
   }

   const calculateGradientPercentage = () => {
      if (ref.current && figureRef.current && (img || imgLarge)) {
         const componentHeight = ref.current.offsetHeight
         const imageHeight = figureRef.current.offsetHeight
         const imageHalfPos = imageHeight / 2
         const imageTopOffset = figureRef.current.offsetTop + imageHalfPos
         const percentage = (imageTopOffset / componentHeight) * 100

         setGradientPercentage(percentage)

      } else if (ref.current && gridContainerRef.current) {
         const componentHeight = ref.current.offsetHeight
         const gridContainerHeight = gridContainerRef.current.offsetHeight
         const gridContainerBottomOffset = gridContainerRef.current.offsetTop + gridContainerHeight
         const percentage = (gridContainerBottomOffset / componentHeight) * 100

         setGradientPercentage(percentage)
      }
   }

   const calculateFigureRightOffset = () => {
      if (gridRowRef.current) {
         const gridRowRect = gridRowRef.current.getBoundingClientRect()
         const offset = document.documentElement.clientWidth - gridRowRect.right
         setFigureRightOffset(offset)
      }
   }

   const calculateMobile = () => {
      setIsMobile(window.innerWidth < 992)
   }

   const handleResize = () => {
      calculateGradientPercentage()
      calculateFigureRightOffset()
      calculateMobile()
   }

   useLayoutEffect(() => {
      handleResize()

      window.addEventListener('resize', handleResize)

      return () => {
         window.removeEventListener('resize', handleResize)
      }
   }, [])

   const picture = (
      <picture>
         <source media="(min-width: 992px)" srcSet={isLarge ? imgLarge?.src : img?.src} />
         <source media="(max-width: 991px)" srcSet={isLarge ? imgLarge?.srcMobile : img?.srcMobile} />
         <img src={imgLarge?.src} alt={img?.alt ? img.alt : 'Image'} />
      </picture>
   )

   const figure = (
      (img || imgLarge) && (
         <figure
            className="landing-page-hero__image"
            style={isLarge ? { right: `${figureRightOffset}px` } : {}}
         >
            {picture}
         </figure>
      )
   )

   // smooth scroll (when clicking the anchor links)
   const smoothScrollTo = useSmoothScroll()

   const smoothScroll = (e) => {
      e.preventDefault()
      const targetId = e.currentTarget.getAttribute('href').substring(1)
      smoothScrollTo(targetId)
   }

   return (
      <div
         ref={ref}
         className={`
            landing-page-hero
            ${isLarge ? 'landing-page-hero--large' : ''}
            ${color ? `landing-page-hero--${color}` : ''}
            ${img || imgLarge ? 'landing-page-hero--image' : ''}
         `}
         style={{
            background: `linear-gradient(to bottom, ${backgroundColor.primary} ${gradientPercentage}%, ${backgroundColor.secondary} ${gradientPercentage}%)`,
         }}
      >
         <div ref={gridContainerRef} className="grid-container" style={{
            paddingBottom: img || imgLarge ? '' : `${isMobile ? '40px' : '80px'}`
         }}>
            <div className="grid-row" ref={gridRowRef}>
               <div className="c-md-8">
                  <div className="landing-page-hero__content">
                     {tag && <p className="landing-page-hero__tag">{tag}</p>}
                     {title && <h1 className="landing-page-hero__title">{title}</h1>}
                     {intro && <div className="landing-page-hero__intro"><RichText content={intro} /></div>}

                     {slidingLink && (
                        <ButtonRegular
                           href={slidingLink.url}
                           text={slidingLink.text}
                           onClick={smoothScroll}
                           className='landing-page-hero__sliding-link'
                           icon={{
                              svg: '<svg width="12" height="14" viewBox="0 0 12 14" xmlns="http://www.w3.org/2000/svg"><path d="M6.69999 0.966675H5.29999L5.29999 10.8101L1.49999 7.01005L0.51004 8L5.99999 13.49L11.4899 8L10.5 7.01005L6.69999 10.8101L6.69999 0.966675Z" fill="currentColor"/></svg>',
                              position: 'right'
                           }}
                        />
                     )}

                  </div>
               </div>

               {img && !isLarge && (
                  <div ref={figureRef} className="c-12">
                     {figure}
                  </div>
               )}
            </div>
         </div>
         {img && isLarge && (
            <div ref={figureRef} className="landing-page-hero__image-wrapper">
               {figure}
            </div>
         )}
      </div>
   )
}
